/**
 * Document Analyzer Global Tool
 *
 * Analyzes file attachments using Claude's vision capabilities.
 * This is a stateless helper tool typically called by other tools
 * (deep research, video script) when they need attachment analysis.
 *
 * Uses the unified document reader service to ensure consistent behavior
 * across all entry points (orchestrator, direct tool, button click).
 */

import { readDocuments, hasAnalyzableFiles } from '../documentReader';
import { finalizeAttachmentReadComplete, formatDocumentToHtml } from '../messaging';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import type { TranslatableContent } from '../../agenticWorkflows/types';

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
  SharedWorkflowContext,
} from '../types';

// ============================================================================
// Tool State
// ============================================================================

/**
 * State for the document analyzer tool.
 * Minimal since this is mostly a stateless operation.
 */
export interface DocumentAnalyzerState {
  /** Whether analysis is currently in progress */
  isAnalyzing: boolean;
  /** Result of the analysis */
  analysisResult?: {
    analysis: string;
    filesAnalyzed: number;
    skippedFiles: string[];
  };
}

// ============================================================================
// Tool Theme
// ============================================================================

const DOCUMENT_ANALYZER_THEME: ToolTheme = {
  accent: '#065f46',      // Emerald 800
  accentBg: '#d1fae5',    // Emerald 100
  border: '#10b981',      // Emerald 500
  subtitle: '#475569',    // Slate 600
  muted: '#64748b',       // Slate 500
};


// ============================================================================
// Step Input/Output Types
// ============================================================================

/**
 * Input for the analyze step.
 */
export interface AnalyzeStepInput {
  /** Files to analyze */
  files: File[];
  /** Context query for the analysis */
  query: string;
  /** Force a fresh analysis, bypassing cache */
  forceRefresh?: boolean;
  /** Whether this is running as part of a workflow chain (hide success message) */
  isChainedWorkflow?: boolean;
}

/**
 * Output from the analyze step.
 */
export interface AnalyzeStepOutput {
  /** The analysis text */
  analysis: string;
  /** Number of files successfully analyzed */
  filesAnalyzed: number;
  /** List of files that were skipped */
  skippedFiles: string[];
  /** Whether result came from cache */
  fromCache?: boolean;
}

// ============================================================================
// Tool Definition
// ============================================================================

/**
 * Document Analyzer Tool Definition.
 *
 * This tool is primarily invoked programmatically by other tools when
 * they need to process attachments. It's not typically triggered via chat.
 *
 * Uses the unified document reader service for consistent behavior.
 */
export const documentAnalyzerTool: GlobalToolDefinition<DocumentAnalyzerState> = {
  id: 'document-analyzer',
  name: 'Document Analyzer',
  description: 'Transcribe file attachments exactly as-is using Claude vision capabilities',

  // Can be triggered by orchestrator or programmatically
  // The orchestrator will trigger this when it detects attachment analysis is needed
  triggerPattern: () => ({ matches: false }), // Orchestrator-only, not direct chat pattern

  createInitialState: (): DocumentAnalyzerState => ({
    isAnalyzing: false,
    analysisResult: undefined,
  }),

  steps: [
    {
      id: 'analyze',
      name: 'Analyze Documents',
      activeDisplayName: 'reading attachment',

      execute: async (
        ctx: StepContext<DocumentAnalyzerState>,
        rawInput?: unknown
      ): Promise<StepResult<AnalyzeStepOutput>> => {
        const input = rawInput as AnalyzeStepInput | undefined;

        // Log attachment sources for debugging
        console.log('[DocumentAnalyzer] Attachment sources:');
        console.log('  - input?.files:', input?.files?.length || 0);
        console.log('  - ctx.triggerMessage.attachments:', ctx.triggerMessage.attachments?.length || 0);

        // Get files from either input or trigger message attachments
        const files = input?.files || ctx.triggerMessage.attachments || [];
        const query = input?.query || ctx.triggerMessage.text || 'Analyze this document';
        const normalizedQuery = query.trim();

        console.log('[DocumentAnalyzer] Total files to process:', files.length);

        // Check for attachments
        if (!files.length) {
          ctx.messaging.showError(
            'No documents attached',
            'Please upload a PDF or image file to analyze.'
          );
          return {
            status: 'failed',
            error: new Error('No documents attached'),
          };
        }

        // Check if files are analyzable
        if (!hasAnalyzableFiles(files)) {
          ctx.messaging.showWarning(
            'Unsupported file type',
            'Supported formats: PDF, DOCX, DOC, TXT, MD, CSV, JSON, XML, HTML, RTF, PNG, JPG, JPEG, GIF, WebP.'
          );
          return {
            status: 'completed',
            output: {
              analysis: '',
              filesAnalyzed: 0,
              skippedFiles: files.map((f) => f.name),
              fromCache: false,
            },
          };
        }

        ctx.setState({ isAnalyzing: true });

        // Show pending widget
        const stream = ctx.messaging.showPending('reading attachment');

        try {
          if (ctx.sharedContext.lastDocumentAnalyzerFilesRef) {
            ctx.sharedContext.lastDocumentAnalyzerFilesRef.current = files;
          }

          // Use unified document reader - handles caching, history, and transactions
          const result = await readDocuments({
            files,
            query,
            signal: ctx.abortSignal,
            sharedContext: ctx.sharedContext,
            recordTransaction: ctx.recordTransaction,
            forceRefresh: !!input?.forceRefresh,
            source: 'direct-tool',
          });

          // Store result in state
          ctx.setState({
            isAnalyzing: false,
            analysisResult: {
              analysis: result.analysis,
              filesAnalyzed: result.filesAnalyzed,
              skippedFiles: result.skippedFiles,
            },
          });

          // Finalize streaming message based on cache status
          if (result.fromCache) {
            console.log('[DocumentAnalyzer] Used cached analysis from unified reader');
            stream.finalize({
              payload: {
                kind: 'document_analyzer.complete',
                toolId: 'document-analyzer',
                data: {
                  filesAnalyzed: result.filesAnalyzed,
                  skippedFiles: result.skippedFiles,
                  fromCache: true,
                },
                summary: `document analyzer: ${result.filesAnalyzed} file${result.filesAnalyzed === 1 ? '' : 's'} analyzed (cached)`,
              },
              sourceTag: 'document_analyzer_cached',
            });
          } else {
            console.log('[DocumentAnalyzer] Fresh analysis via unified reader');
            // Use global helper for standardized attachment read confirmation with timing/info
            finalizeAttachmentReadComplete(stream, {
              analysis: result.analysis,
              filesAnalyzed: result.filesAnalyzed,
              skippedFiles: result.skippedFiles,
              tokenUsage: result.tokenUsage,
            }, ctx.recordTransaction, {
              sourceTag: 'document_analyzer_attachment_read',
              transactionSummary: 'document analysis',
            });
          }

          // Only show document analysis output for standalone document analysis
          // When part of a chain (e.g., document-analyzer → translate), hide this message
          // as it's redundant - the user will see output from the final workflow in the chain
          if (!input?.isChainedWorkflow) {
            // Display the analysis directly as plain formatted text (no special message template)
            // This provides a clean, unified output without headers, icons, or action buttons
            const analysisHtml = formatDocumentToHtml(result.analysis);
            ctx.messaging.addSystemMessage({
              text: result.analysis,
              html: analysisHtml,
              sourceTag: 'document_analysis_result',
            });
          } else {
            console.log('[DocumentAnalyzer] Skipping output message - part of workflow chain');
          }

          // Store transcription in sharedContext for downstream tools (translate, format-report, etc.)
          // This enables workflow chaining like [document-analyzer → translate]
          const translatableData: TranslatableContent = {
            content: result.analysis,
            title: 'Document Transcription',
            timestamp: new Date(),
            source: 'document_analysis',
          };
          ctx.sharedContext.setLastTranslatableContent(translatableData);
          ctx.sharedContext.lastTranslatableContentRef.current = translatableData;
          console.log('[DocumentAnalyzer] Stored analysis in sharedContext for downstream tools');

          return {
            status: 'completed',
            output: {
              analysis: result.analysis,
              filesAnalyzed: result.filesAnalyzed,
              skippedFiles: result.skippedFiles,
              fromCache: result.fromCache,
            },
          };
        } catch (error) {
          ctx.setState({ isAnalyzing: false });

          // Finalize with structured error event (renderer composes the user reply)
          const docAnalyzerErrMsg = error instanceof Error ? error.message : 'the attachment could not be processed';
          const userFriendlyError = /timeout|503|502|500|network|fetch/i.test(docAnalyzerErrMsg)
            ? 'The attachment could not be analyzed right now due to a temporary service issue.'
            : 'The attachment could not be analyzed right now.';
          const attachmentNames = files.map((file) => file.name).filter(Boolean);

          if (normalizedQuery.length > 0) {
            const fallbackTitle = attachmentNames.length > 0
              ? attachmentNames.join(', ')
              : 'Document (analysis failed)';
            const fallbackContent: TranslatableContent = {
              content: normalizedQuery,
              title: fallbackTitle,
              timestamp: new Date(),
              source: 'document_analysis_failed',
            };
            ctx.sharedContext.setLastTranslatableContent(fallbackContent);
            ctx.sharedContext.lastTranslatableContentRef.current = fallbackContent;
            console.log('[DocumentAnalyzer] Stored degraded fallback content for downstream workflows');
          }

          const retryButtonHtml = `<div style="margin-top:12px;display:flex;gap:8px;flex-wrap:wrap;">
            <button data-button-id="global-tool-document-analyzer-retry-btn" style="padding:8px 16px;background:#1e40af;color:white;border:none;border-radius:6px;cursor:pointer;font-size:0.85rem;">retry analysis</button>
          </div>`;

          stream.finalize({
            payload: {
              kind: 'document_analyzer.error',
              toolId: 'document-analyzer',
              severity: 'error',
              data: {
                errorMessage: docAnalyzerErrMsg,
                userFriendlyError,
                phase: 'analysis',
                fileNames: attachmentNames,
              },
              summary: `document analyzer failed for ${attachmentNames.join(', ') || 'attachment'}: ${docAnalyzerErrMsg}`,
            },
            workflowActionButtonsHtml: retryButtonHtml,
            sourceTag: 'attachment_read_error',
          });

          // Return failed status (hard failure for standalone document analysis)
          return {
            status: 'failed',
            error: error instanceof Error ? error : new Error('Document analysis failed'),
          };
        }
      },

      // No validateInput - let execute function handle validation via fallback
      // The execute function checks: input?.files || ctx.triggerMessage.attachments || []
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: DOCUMENT_ANALYZER_THEME,

  buttonIds: {
    retry: 'global-tool-document-analyzer-retry-btn',
  },

  canTriggerFromButton: false,
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a message has files that can be analyzed.
 * Re-exports the underlying utility for convenience.
 */
export { hasAnalyzableFiles };

/**
 * Analyze documents directly (without going through the tool runtime).
 * Useful for embedding document analysis within other tools' steps.
 *
 * Uses the unified document reader service for consistent behavior.
 */
export async function analyzeDocuments(params: {
  files: File[];
  query: string;
  signal?: AbortSignal;
  sharedContext: SharedWorkflowContext;
  recordTransaction?: (params: {
    tokenUsage: import('../../../components/GlobalChatSystem/types').TokenUsageMetadata;
    source: string;
    modelOverride?: string;
    summary?: string;
  }) => void;
}): Promise<AnalyzeStepOutput> {
  const { files, query, signal, sharedContext, recordTransaction } = params;

  if (!files?.length || !hasAnalyzableFiles(files)) {
    return {
      analysis: '',
      filesAnalyzed: 0,
      skippedFiles: files?.map((f) => f.name) || [],
      fromCache: false,
    };
  }

  // Use unified document reader
  const result = await readDocuments({
    files,
    query,
    signal,
    sharedContext,
    recordTransaction,
    source: 'workflow', // Called from within a workflow
  });

  return {
    analysis: result.analysis,
    filesAnalyzed: result.filesAnalyzed,
    skippedFiles: result.skippedFiles,
    fromCache: result.fromCache,
  };
}
